import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import rtlPlugin from 'stylis-plugin-rtl';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import CommonLayout from '../../components/CommonLayout';
import Loader from '../../components/Loader';
import DocumentRenderer from './DocumentRenderer';
import DocumentDetailsDialog from './DocumentDetailsDialog';
import { getDocument, getDocumentFile, getFolders } from './departmentDocumentsApi';
import { folderPathLabel } from './libraryTree';
// The protocol viewer's toolbar, PDF and placeholder styles, reused as is.
import '../protocols/ProtocolViewer.scss';
import './DepartmentDocumentViewer.scss';

const cacheRtl = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const theme = createTheme({ typography: { fontFamily: '"Rubik", sans-serif' }, direction: 'rtl' });

// Opening a document goes straight to the file — no intermediate details page
// as protocols have. The details live behind the "פרטים" button.
const DepartmentDocumentViewer = () => {
  const { id } = useParams();
  const [document, setDocument] = useState(null);
  const [blob, setBlob] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [folderLabel, setFolderLabel] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      setError('');
      setBlob(null);
      try {
        const [meta, file] = await Promise.all([getDocument(id), getDocumentFile(id)]);
        if (cancelled) return;
        setDocument(meta);
        setBlob(file);
      } catch (err) {
        console.error('Loading department document failed:', err);
        if (!cancelled) setError(err?.serverError?.hebMessage || 'טעינת המסמך נכשלה');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const openDetails = async () => {
    setShowDetails(true);
    // The folder path needs the folder tree, only worth fetching when asked for.
    if (document?.folder && !folderLabel) {
      try {
        const folders = await getFolders(document.department);
        setFolderLabel(folderPathLabel(folders, document.folder));
      } catch { /* the dialog just shows no folder */ }
    }
  };

  const handleDownload = useCallback(() => {
    if (!blob || !document) return;
    const url = URL.createObjectURL(blob);
    const a = window.document.createElement('a');
    a.href = url;
    a.download = document.file?.originalName || document.name;
    window.document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [blob, document]);

  return (
    <CommonLayout>
      <CacheProvider value={cacheRtl}>
        <ThemeProvider theme={theme}>
          <div className="protocol-viewer-container department-document-viewer" data-page-title={document?.name || undefined}>
            {isLoading && <Loader isLoading={true} />}

            {!isLoading && error && (
              <div className="error-container"><p className="error-message">{error}</p></div>
            )}

            {!isLoading && !error && document && blob && (
              <>
                <div className="file-navigation">
                  <div className="file-nav-controls">
                    <div className="file-nav-actions-right">
                      <button type="button" onClick={handleDownload} className="nav-button">
                        <FontAwesomeIcon icon={faDownload} /> הורד
                      </button>
                      <button type="button" onClick={openDetails} className="nav-button">
                        <FontAwesomeIcon icon={faInfoCircle} /> פרטים
                      </button>
                    </div>
                    <div className="file-nav-info">
                      <span>{document.name}</span>
                    </div>
                  </div>
                </div>
                <div className="dd-viewer-body">
                  <DocumentRenderer blob={blob} fileName={document.file?.originalName} onDownload={handleDownload} />
                </div>
              </>
            )}

            <DocumentDetailsDialog
              open={showDetails}
              document={document}
              folderPath={document?.folder ? folderLabel : 'מסמכי המחלקה'}
              onClose={() => setShowDetails(false)}
            />
          </div>
        </ThemeProvider>
      </CacheProvider>
    </CommonLayout>
  );
};

export default DepartmentDocumentViewer;
